import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { setNonstops, selectNonstops } from '../../features/nonstop/nonstopSlice';
import useFirestore from '../../hooks/useFirestore';
import useFavorites from '../../hooks/useFavorites';
import useSearch from '../../hooks/useSearch';
import FirestoreError from '../common/FirestoreError';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaInfoCircle, FaTv } from 'react-icons/fa';
import SearchInput from '../common/SearchInput';
import Nonstops from '../audio/Songs/Nonstops';
import GoogleAd2 from '../GoogleAd2';
import Nav3 from '../Navs/SideView/Nav3';
import ViewCounter from '../ViewCounter';

// Styled components for enhanced UI
const PageContainer = styled.div`
  background-color: #030617;
  background-image: linear-gradient(to bottom, #030617, #0a1128);
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 200px;
    background: linear-gradient(to bottom, rgba(75, 192, 192, 0.05), transparent);
    z-index: 0;
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  position: relative;
  z-index: 1;
`;

const Header = styled(motion.div)`
  text-align: center;
  margin: 30px 0;
  color: white;
  
  h1 {
    font-size: 2.5rem;
    margin-bottom: 10px;
    background: linear-gradient(to right, #fff, #4bc0c0);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    
    @media (max-width: 768px) {
      font-size: 1.8rem;
    }
  }
  
  p {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.7);
    max-width: 600px;
    margin: 0 auto;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
`;



const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: white;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(75, 192, 192, 0.1);
  border-top-color: rgb(75, 192, 192);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 30px;
  background: rgba(255, 0, 0, 0.1);
  border-radius: 10px;
  margin: 30px auto;
  max-width: 500px;
  color: white;
  
  h3 {
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    svg {
      margin-right: 10px;
      color: #ff6b6b;
    }
  }
  
  p {
    margin-bottom: 20px;
    color: rgba(255, 255, 255, 0.8);
  }
  
  button {
    background: rgba(75, 192, 192, 0.2);
    color: rgb(75, 192, 192);
    border: 1px solid rgba(75, 192, 192, 0.3);
    padding: 8px 16px;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      background: rgba(75, 192, 192, 0.3);
    }
  }
`;

function TVchannels() {
  const dispatch = useDispatch();
  const nonstops = useSelector(selectNonstops);
  const { data: channels, loading: firestoreLoading, error, retry } = useFirestore('channels');
  const { favorites } = useFavorites('favoriteChannels');
  const { searchTerm, handleSearchChange, clearSearch } = useSearch(nonstops, ['title', 'description']);
  
  // Simplified state management
  const [componentState, setComponentState] = useState({
    loading: true,
    showError: false,
    showContent: false,
    showEmpty: false
  });

  // Determine what to show based on current state
  useEffect(() => {
    // If we have channels data, show content
    if (nonstops && nonstops.length > 0) {
      setComponentState({
        loading: false,
        showError: false,
        showContent: true,
        showEmpty: false
      });
      return;
    }
    
    // If Firestore is done loading
    if (!firestoreLoading) {
      // If we have an error and no data, show error
      if (error && (!nonstops || nonstops.length === 0)) {
        setComponentState({
          loading: false,
          showError: true,
          showContent: false,
          showEmpty: false
        });
        return;
      }
      
      // If we have no data and no error, show empty
      if (!nonstops || nonstops.length === 0) {
        setComponentState({
          loading: false,
          showError: false,
          showContent: false,
          showEmpty: true
        });
        return;
      }
    }
    
    // Update Redux store when we have channels data
    if (channels && channels.length > 0) {
      dispatch(setNonstops(channels));
    }
  }, [channels, nonstops, firestoreLoading, error, dispatch]);
  
  // Handle retry
  const handleRetry = () => {
    setComponentState({
      loading: true,
      showError: false,
      showContent: false,
      showEmpty: false
    });
    retry();
  };
  


  return (
    <PageContainer>
      <Helmet>
        <title>Watch TV Channels in Western Uganda</title>
        <meta
          name="description"
          content="Stream live TV channels from Western Uganda. Watch your favorite TV stations online with high-quality streaming."
        />
        <meta name="keywords" content="tv channels, western uganda, online tv, stream tv, kasese tv, fort portal tv, live tv" />
        <link rel="apple-touch-icon" href="/logo512.jpg" />
      </Helmet>

      <Nav3 />
      
      <ContentContainer>
        <Header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1>TV Channels</h1>
          <p>Stream live television from Western Uganda and stay connected to local news, shows, and entertainment</p>
        </Header>
        
        <SearchInput 
          value={searchTerm}
          onChange={handleSearchChange}
          onClear={clearSearch}
          placeholder="Search TV channels..."
        />
        
        <AnimatePresence mode="wait">
          {componentState.loading && (
            <LoadingContainer key="loading">
              <LoadingSpinner />
              <p>Loading TV channels...</p>
            </LoadingContainer>
          )}
          
          {componentState.showContent && (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Nonstops searchTerm={searchTerm} favorites={favorites} />
            </motion.div>
          )}
          
          {componentState.showError && (
            <ErrorContainer key="error">
              <h3><FaInfoCircle /> Error</h3>
              <p>{error === 'quota-exceeded' ? 'Firestore quota exceeded. Please try again later.' : 'Failed to load TV channels.'}</p>
              <button onClick={handleRetry}>Try Again</button>
            </ErrorContainer>
          )}
          
          {componentState.showEmpty && (
            <ErrorContainer key="empty">
              <h3><FaInfoCircle /> No Channels</h3>
              <p>No TV channels are currently available. Please check back later.</p>
              <button onClick={handleRetry}>Refresh</button>
            </ErrorContainer>
          )}
        </AnimatePresence>
      </ContentContainer>

      <GoogleAd2 />
      <ViewCounter path="/channels" />
    </PageContainer>
  );
}

export default TVchannels
